import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import Warranty from 'components/lab-order/warranty/warranty.component';
import SelectAction from './select-action/select-action.component';
import { ActionSet } from 'models/action-set';
import { Action } from 'models/action';
import { ActionConstruction } from 'models/order-input/action-order';
import ConstructionForm from '../construction-form/construction-form.component';
import { emptyConstruction } from '../construction-form/construction-form-helper';
import { useConstructions } from 'context/construction-context';
import TandvardCheckbox from '../tandvard-checkbox/tandvard-checkbox.component';
import { customerSettings } from 'utils/customerSettings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CONSTANTS } from 'config/constants';

type AddConstructionDialogProps = {
    open: boolean;
    onClose: () => void;
    actionSet: ActionSet;
    onAddConstruction?: (construction: ActionConstruction) => void;
};
const AddConstructionDialog: FC<AddConstructionDialogProps> = ({ open, onClose, actionSet, onAddConstruction }) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const [activeStep, setActiveStep] = useState(0);
    const { isConstructionValid, primaryAction, construction, setConstruction } = useConstructions();
    // primaryAction falls back to an empty object, so check the chosen actions instead
    const isPrimaryActionSelected = Boolean(construction.actions?.length);

    const currentOrg = currentOrgVar();

    const isTioTandvardPricingEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING,
    );

    useEffect(() => {
        if (open) {
            setConstruction(emptyConstruction());
            setActiveStep(0);
        }
    }, [open]);

    const handleClose = () => {
        onClose();
    };

    const handlePrimaryActionSelected = (action: Action, categoryId: string, subCategoryId: string): void => {
        setConstruction((prev) => ({
            ...prev,
            actions: [action],
            categoryId,
            subCategoryId,
        }));
    };

    const handlePrimaryActionCleared = (): void => {
        setConstruction((prev) => ({
            ...prev,
            actions: [],
            categoryId: undefined,
            subCategoryId: undefined,
        }));
    };

    const handleConstructionChanged = (construction: ActionConstruction) => {
        setConstruction((prev) => ({
            ...prev,
            ...construction,
            warranty: prev.warranty || false,
            ...(prev.warrantyType ? { warrantyType: prev.warrantyType } : {}),
        }));
    };

    const handleHandleTandvardChange = (isTioTandvard: boolean) => {
        setConstruction((prev) => ({ ...prev, isTioTandvard }));
    };

    const handleHandleWarrantyChange = (warranty: boolean, warrantyType: string) => {
        setConstruction((prev) => ({ ...prev, warranty, warrantyType }));
    };

    const handleCreateButtonClicked = () => {
        if (onAddConstruction) {
            onAddConstruction(construction);
        }
        handleClose();
    };

    const getStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <SelectAction
                        actionSet={actionSet}
                        onActionSelected={handlePrimaryActionSelected}
                        onActionCleared={handlePrimaryActionCleared}
                    />
                );
            case 1:
                return (
                    <ConstructionForm
                        construction={construction}
                        actionSet={actionSet}
                        onConstructionChanged={handleConstructionChanged}
                    />
                );
            default:
                return 'Unknown stepIndex';
        }
    };

    const categoryInfo = () => {
        if (!isPrimaryActionSelected) {
            return undefined;
        }
        return primaryAction.mainCategory?.instructions ? (
            <Alert severity="info">{primaryAction.mainCategory.instructions}</Alert>
        ) : undefined;
    };

    return (
        <DHDialog
            key={open ? 'open' : 'closed'}
            open={open}
            onClose={handleClose}
            disableBackdropClick
            maxWidth={activeStep === 0 ? 'lg' : 'xl'}
            fullWidth
            title={activeStep === 0 ? translate('orders:SELECT_ACTION') : translate('orders:CONSTRUCTION')}
            actions={
                <Box width="100%">
                    <Box width="100%" mb={1}>
                        {categoryInfo()}
                    </Box>

                    {construction.isTioTandvard && isTioTandvardPricingEnabled && (
                        <Box width="100%" mb={1}>
                            <Alert severity="info">{translate('orders:TIOTANDVARD_NOTE')}</Alert>
                        </Box>
                    )}

                    <Box width="100%" display="flex" justifyContent="flex-end" alignItems="center">
                        <Box display="flex" gap={1}>
                            {activeStep === 1 ? (
                                <>
                                    {isTioTandvardPricingEnabled && isPrimaryActionSelected && (
                                        <TandvardCheckbox
                                            handleTandvardCheckbox={handleHandleTandvardChange}
                                            isTioTandvard={construction.isTioTandvard || false}
                                        />
                                    )}

                                    <Warranty
                                        handleWarranty={handleHandleWarrantyChange}
                                        warranty={construction.warranty || false}
                                        warrantyType={construction.warrantyType || ''}
                                    />

                                    <Button
                                        variant="contained"
                                        disabled={!isConstructionValid}
                                        onClick={handleCreateButtonClicked}
                                    >
                                        {translate('orders:ADD_CONSTRUCTION')}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    disabled={!isPrimaryActionSelected}
                                    variant="contained"
                                    onClick={() => setActiveStep(1)}
                                >
                                    {translate('common:NEXT_STEP')}
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            }
        >
            <Box>{getStepContent()}</Box>
        </DHDialog>
    );
};

export default AddConstructionDialog;
